import { useState } from 'react';

import { useZoomPan } from './useZoomPan.js';
import { ViewBar } from './ViewBar.js';

/**
 * An image you can zoom (wheel, toward the cursor), pan (drag) and refit
 * (double click or Fit). Fits the area on load and on resize. `pixelated`
 * keeps pixel art crisp at every zoom.
 */
export function ZoomableImage({ src, alt, pixelated = false }: { src: string; alt: string; pixelated?: boolean }) {
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);
  const [failed, setFailed] = useState(false);
  const zoom = useZoomPan({ content: size });
  const { view } = zoom;

  return (
    <div
      ref={zoom.boxRef}
      className={`gen-pv-box${zoom.dragging ? ' is-dragging' : ''}`}
      role="group"
      aria-label={`${alt}. Scroll to zoom, drag to pan, double click to fit. Keys: plus, minus, 0, arrows.`}
      {...zoom.boxProps}
    >
      {failed ? (
        <span className="muted gen-pv-msg">Could not load the preview.</span>
      ) : (
        <img
          className={`gen-pv-img${pixelated ? ' gen-pixelated-strict' : ''}`}
          alt={alt}
          draggable={false}
          src={src}
          style={
            size
              ? { width: size.width, height: size.height, transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})` }
              : { visibility: 'hidden' }
          }
          onLoad={(e) => setSize({ width: e.currentTarget.naturalWidth || 1, height: e.currentTarget.naturalHeight || 1 })}
          onError={() => setFailed(true)}
        />
      )}
      <ViewBar onOut={() => zoom.zoomBy(0.8)} onIn={() => zoom.zoomBy(1.25)} onFit={zoom.fit} />
    </div>
  );
}
